import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, MessageCircle, Check, Loader2, Phone, Navigation } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { useServices } from "@/lib/queries";
import { useSettings } from "@/context/SettingsContext";
import { whatsappLink, telLink, mapsLink } from "@/lib/contact";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full border border-white/12 bg-neutral-900 px-4 py-3 text-sm text-white placeholder:text-neutral-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold transition-colors";
const labelCls = "mb-2 block font-oswald text-[11px] uppercase tracking-[0.15em] text-neutral-400";

export default function Appointment() {
  const [params] = useSearchParams();
  const settings = useSettings();
  const { data: services = [] } = useServices();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    car: "",
    service: params.get("hizmet") || "",
    preferred_day: "",
    note: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Lütfen ad soyad ve telefon bilgisini girin.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", form);
      setSent(true);
      toast.success("Randevu talebiniz alındı! En kısa sürede sizi arayacağız.");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }

  function sendWhatsApp() {
    const msg = `Merhaba, randevu almak istiyorum.\nAd Soyad: ${form.name}\nTelefon: ${form.phone}\nAraç: ${form.car}\nHizmet: ${form.service}\nTercih edilen gün: ${form.preferred_day}\nNot: ${form.note}`;
    window.open(whatsappLink(settings, msg), "_blank");
  }

  return (
    <>
      <Seo title="Hızlı Randevu | Meguiar's İzmit" description="Aracınız için hızlı randevu oluşturun. Ücretsiz değerlendirme ve en doğru koruma planı için bize ulaşın." canonicalPath="/randevu" />
      <PageHero
        overline="Hızlı Randevu"
        title="Aracınız için randevu oluşturun"
        subtitle="Formu doldurun; talebiniz hem ekibimize ulaşsın hem de dilerseniz WhatsApp üzerinden hızlıca iletin."
        breadcrumbs={[{ label: "Ana Sayfa", to: "/" }, { label: "Randevu" }]}
        compact
      />

      <section className="obsidian py-16 sm:py-24">
        <div className="container-x grid gap-8 lg:grid-cols-[1.25fr_0.75fr]">
          <div className="border border-white/10 bg-gradient-to-b from-neutral-900 to-[#070707] p-6 sm:p-9">
            {sent ? (
              <div data-testid="appointment-success" className="flex flex-col items-center py-12 text-center">
                <span className="flex h-16 w-16 items-center justify-center border border-gold/40 bg-gold/10 text-gold"><Check className="h-8 w-8" /></span>
                <h2 className="mt-6 font-display text-2xl font-medium text-white">Talebiniz alındı!</h2>
                <p className="mt-3 max-w-md text-neutral-400">En kısa sürede sizinle iletişime geçeceğiz. Dilerseniz WhatsApp'tan da yazabilirsiniz.</p>
                <button onClick={sendWhatsApp} className="mt-6 inline-flex items-center gap-2 bg-gold px-6 py-3.5 font-oswald text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-gold-300">
                  <MessageCircle className="h-[18px] w-[18px]" /> WhatsApp'tan da İlet
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="appointment-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>Ad Soyad *</label>
                    <input data-testid="appt-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Adınız Soyadınız" />
                  </div>
                  <div>
                    <label className={labelCls}>Telefon *</label>
                    <input data-testid="appt-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="05XX XXX XX XX" />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>Araç Marka / Model</label>
                    <input data-testid="appt-car" className={inputCls} value={form.car} onChange={set("car")} placeholder="Örn. BMW 5 Serisi" />
                  </div>
                  <div>
                    <label className={labelCls}>Hizmet</label>
                    <select data-testid="appt-service" className={inputCls} value={form.service} onChange={set("service")}>
                      <option value="">Hizmet seçin</option>
                      {services.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
                      <option value="Diğer">Diğer / Emin değilim</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Tercih Edilen Gün</label>
                  <input data-testid="appt-day" className={inputCls} value={form.preferred_day} onChange={set("preferred_day")} placeholder="Örn. Hafta içi / Cumartesi" />
                </div>
                <div>
                  <label className={labelCls}>Not</label>
                  <textarea data-testid="appt-note" rows={4} className={inputCls} value={form.note} onChange={set("note")} placeholder="Eklemek istedikleriniz" />
                </div>

                <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                  <button type="submit" disabled={loading} data-testid="appt-submit" className="inline-flex flex-1 items-center justify-center gap-2 bg-gold px-6 py-3.5 font-oswald text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-gold-300 disabled:opacity-60">
                    {loading ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <CalendarCheck className="h-[18px] w-[18px]" />} Randevu Talebi Gönder
                  </button>
                  <button type="button" onClick={sendWhatsApp} data-testid="appt-whatsapp" className="inline-flex items-center justify-center gap-2 border border-white/15 px-6 py-3.5 font-oswald text-sm font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:border-gold/50">
                    <MessageCircle className="h-[18px] w-[18px] text-gold" /> WhatsApp ile Gönder
                  </button>
                </div>
              </form>
            )}
          </div>

          <aside className="space-y-4">
            <div className="border border-white/10 bg-white/[0.02] p-6">
              <h3 className="font-display text-lg font-medium text-white">Neden randevu?</h3>
              <ul className="mt-4 space-y-3 text-sm text-neutral-300">
                {["Aracınıza acele etmeden, hak ettiği özen gösterilir", "Doğru ürün ve süre planlaması yapılır", "Beklemeden, planlı şekilde işlem başlar"].map((t) => (
                  <li key={t} className="flex items-start gap-3"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="border border-gold/25 bg-gold/[0.04] p-6">
              <p className="font-oswald text-[11px] uppercase tracking-[0.2em] text-neutral-400">Hızlı iletişim</p>
              <p className="mt-1 font-display text-2xl font-medium text-white">{settings?.phone}</p>
              <div className="mt-4 space-y-2.5">
                <a href={whatsappLink(settings)} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center gap-2 bg-gold px-5 py-3 font-oswald text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-gold-300">
                  <MessageCircle className="h-[18px] w-[18px]" /> WhatsApp'tan Yaz
                </a>
                <a href={telLink(settings)} className="inline-flex w-full items-center justify-center gap-2 border border-white/15 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-gold/40">
                  <Phone className="h-[18px] w-[18px] text-gold" /> Hemen Ara
                </a>
                <a href={mapsLink(settings)} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center gap-2 border border-white/15 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-gold/40">
                  <Navigation className="h-[18px] w-[18px] text-gold" /> Yol Tarifi
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
